import { useRouter } from 'expo-router'
import { useEffect, type ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import { View } from 'react-native'

import { Button } from '@/src/elements/button/default'
import { Image } from '@/src/elements/image'
import { useMeQuery } from '@/src/features/auth/services/useMeQuery'
import { NOTIFICATION_CATEGORY } from '@/src/features/notification/models/notification.constants'
import { useNotificationSettingsMutation } from '@/src/features/notification/services/useNotificationSettingsMutation'
import { TEST_ID } from '@/src/general/constants/testId'
import { ICON } from '@/src/general/enums/icons'
import { GTM } from '@/src/general/utils/gtm'
import { EVENTS } from '@/src/general/utils/gtm/constants'
import { parseIdDash } from '@/src/general/utils/parseIdDash'
import { useTranslate } from '@/src/lang/useTranslate'

import { useCommunityQuery } from '../../services/useCommunityQuery'
import { useCommunityJoinSteps } from '../hooks/useCommunityJoinSteps'
import { SubscribeJourneyLayout } from '../layouts/SubscribeJourneyLayout'

const mailIllustrationByLocale = {
  en: require('@/img/community/mails/en.png'),
  fr: require('@/img/community/mails/fr.png'),
  es: require('@/img/community/mails/es.png'),
  pt: require('@/img/community/mails/pt.png'),
}

export const CommunityJoinSetupMailsScreen = ({
  idSlug,
  searchParams,
}: TSearchParamsParam & TIdSlugParam): ReactNode => {
  const [id] = parseIdDash(idSlug)
  const { t } = useTranslate()
  const router = useRouter()
  const { data: community } = useCommunityQuery(id)
  const { data: me } = useMeQuery()
  const { goToNextStep, stepperOptions, shouldBeDisplayed } = useCommunityJoinSteps({
    idSlug,
    stepName: 'email',
    searchParams,
  })

  useEffect(() => GTM.sendEvent(EVENTS.COMMUNITY_JOINED_EMAILS_DISPLAYED), [])

  let imageSource = mailIllustrationByLocale['en']
  if (me?.lang) {
    imageSource = me.lang !== 'pl' && me.lang !== 'it' ? mailIllustrationByLocale[me.lang] : imageSource
  }

  const { mutate: editNotificationSettings } = useNotificationSettingsMutation(NOTIFICATION_CATEGORY.PUBLICATIONS)

  const handleAcceptEmails = () => {
    GTM.sendEvent(EVENTS.COMMUNITY_JOINED_EMAILS_ACCEPTED)
    editNotificationSettings({ [id]: { email: true } })
    goToNextStep()
  }

  const handleNoThanks = () => {
    GTM.sendEvent(EVENTS.COMMUNITY_JOINED_EMAILS_REFUSED)
    editNotificationSettings({ [id]: { email: false } })
    goToNextStep()
  }

  if (!shouldBeDisplayed) return null

  return (
    <SubscribeJourneyLayout
      testID={TEST_ID.COMMUNITY.JOIN_SETUP_MAILS_SCREEN}
      icon={ICON.MAIL}
      stepperOptions={stepperOptions}
      title={community && t('community.join-mails.title', { lifetime: community.lifetime })}
      subtitle={<FormattedMessage id="community.join-mails.subtitle" />}
      onBack={router.back}
      footer={
        <View className="gap-4">
          <Button colors="primary" variant="solid" className="mt-6 w-full" onPress={handleAcceptEmails}>
            <FormattedMessage id="community.join-mails.activation" />
          </Button>
          <Button colors="primary" variant="faded" className="w-full" onPress={handleNoThanks}>
            <FormattedMessage id="words.no-thanks2" />
          </Button>
        </View>
      }
    >
      <View className="w-full rounded-lg shadow-sm" style={{ aspectRatio: 1026 / 234 }}>
        <Image radius="lg" source={imageSource} />
      </View>
    </SubscribeJourneyLayout>
  )
}
